<script setup lang="ts">
import { computed, ref } from 'vue'
import Button from 'primevue/button'
import { useGlobalStore } from '@/stores/globalStore'
import { useAuth } from '@/lib/utils/useAuth'
import { useI18n } from 'vue-i18n'
import router from '@/router'

/**
 * Persistent "you are viewing as someone else" bar, mounted in the
 * authenticated layout chrome so it is present on EVERY route.
 *
 * This is the main safety control of the impersonation feature: an
 * impersonated session has the target's full write access, so the operator
 * must never be able to forget whose account they are acting in. Hence
 * always-visible rather than a toast, and the revert control lives here
 * rather than buried in a menu.
 *
 * State is server-owned (`globalStore.impersonation`, hydrated from
 * `/api/auth/impersonation`), so a hard refresh re-renders this instead of
 * silently stranding the operator inside the other account.
 */
const globalStore = useGlobalStore()
const { stopImpersonation } = useAuth()
const { t } = useI18n()

const reverting = ref<boolean>(false)

const state = computed(() => globalStore.impersonation)

const viewingAs = computed<string>(() => {
  const user = globalStore.user.detail
  const name = [user?.first_name, user?.last_name].filter(Boolean).join(' ').trim()
  return name !== '' ? name : (user?.email ?? '')
})

const operator = computed<string>(() => {
  const who = state.value.impersonator
  if (!who) return ''
  const name = [who.first_name, who.last_name].filter(Boolean).join(' ').trim()
  return name !== '' ? name : (who.email ?? '')
})

const revert = async (): Promise<void> => {
  if (reverting.value) return
  reverting.value = true
  try {
    const ok = await stopImpersonation()
    if (ok) {
      // Land somewhere every operator can see: the impersonated route may
      // not exist for, or may 403 under, the restored identity.
      await router.push({ name: 'Dashboard' })
    }
  } finally {
    reverting.value = false
  }
}
</script>

<template>
  <div
    v-if="state.active"
    class="impersonation-banner bg-warning shrink-0 flex items-center justify-center gap-3 px-4 py-2 text-sm font-semibold"
    role="status"
    aria-live="polite"
  >
    <i class="pi pi-eye" aria-hidden="true" />
    <span>
      {{ t('impersonation.banner.viewingAs', { name: viewingAs }) }}
      <template v-if="operator">
        {{ t('impersonation.banner.asOperator', { operator }) }}
      </template>
    </span>
    <Button
      :label="t('impersonation.banner.revert')"
      icon="pi pi-arrow-left"
      size="small"
      severity="contrast"
      :loading="reverting"
      @click="revert"
    />
  </div>
</template>
